import { useCardData } from 'components/providers/CardDataProvider'
import { getInventoryStatusUpdate, getPtProductsDetails } from 'lib/api'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { unstable_batchedUpdates } from 'react-dom'
import {
  ArticleParams,
  CardmarketCondition,
  CountryEdition,
  countryEditionMap,
  GRADING_COMPANY_LABELS,
  groupByPtArticle,
  InventoryArticle,
  isInventoryStale,
  isSingle,
  mapBackendFinishAttributeToInternal,
} from 'shared'
import { FetchedStockInterface, useStockStore } from './useStockStore'

type UseFetchInventoryDiffOptions = {
  onPollingError?: (err: unknown) => void
  fetchedStock: FetchedStockInterface
  setFetchedStock: (
    newValue: FetchedStockInterface | ((arg: FetchedStockInterface) => FetchedStockInterface)
  ) => void
}

export const useFetchInventoryDiff = ({
  onPollingError,
  fetchedStock,
  setFetchedStock,
}: UseFetchInventoryDiffOptions) => {
  const cardData = useCardData()
  const { setRefreshedStock, setStockLock } = useStockStore()

  const [setZustandStock] = useStockStore((state) => [state.setFetchedStock])

  const [isProcessing, setIsProcessing] = useState(false)

  const pollingRef = useRef<NodeJS.Timeout | null>(null) // to know if there is already an active setInterval

  // This is used to keep the state updated within setInterval, otherwise setInterval would always read the function's construction state and never read any updates
  const isFetchingRef = useRef(false) // to avoid launching two fetchInventoryStatus in parallel

  const fetchedStockRef = useRef(fetchedStock) //to always have the latest snapshot of the persistent stock inside the async

  useEffect(() => {
    fetchedStockRef.current = fetchedStock
  }, [fetchedStock])

  const cardDataMap = useMemo(() => {
    const map = new Map<number, any>()
    for (const card of cardData) {
      map.set(card._id, card)
    }
    return map
  }, [cardData])

  // Call if you want to poll until isProcessing becomes false
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
  }, [])

  const buildAttributes = (a: any, p: any): ArticleParams['attributes'] => [
    a.locations.reduce((sum: number, l: any) => sum + (l.quantity ?? 0), 0),
    p.condition as CardmarketCondition,
    p.idLanguage,
    Number(p.mkmProductId),
    a.baseSellPrice,
    a.comment || '',
    false,
    p.isSigned,
    p.isFirstEd,
    '',
    null,
    false,
    GRADING_COMPANY_LABELS.find((e) => e.code === p.gradingCompany)?.code,
    p.grade,
    countryEditionMap.find((e) => e.code === p.countryEdition)?.value ?? CountryEdition.Regular,
    mapBackendFinishAttributeToInternal(p.finishType),
    Number(a._id),
  ]

  const mergeStock = async (currentStock: FetchedStockInterface, articleData: Map<number, any>) => {
    let maxProcessedAt: string | null = null

    const currentAll = groupByPtArticle([...currentStock.stock, ...currentStock.sealedStock])

    const pendingByPtProductId = new Map<number, any>()

    for (const [id, d] of articleData) {
      if (
        d.lastPtTransactionProcessedAt &&
        (!maxProcessedAt || d.lastPtTransactionProcessedAt > maxProcessedAt)
      ) {
        maxProcessedAt = d.lastPtTransactionProcessedAt
      }

      const matchingArticle = currentAll.get(id)

      if (matchingArticle) {
        // update matchingArticle fields
        matchingArticle.comment = d.comment
        matchingArticle.price = d.baseSellPrice
        matchingArticle.ptArticleReservedQuantity = d.reservedQuantity
        matchingArticle.marginTaxationQuantity = d.marginTaxationQuantity
        matchingArticle.standardTaxationQuantity = d.standardTaxationQuantity

        // Used classic for instead of Array.reduce for performance reasons.
        // The for:
        // - does not create callbacks or closures on each iteration (less GC pressure)
        // - avoids the call overhead of reduce()
        // - is better optimized by the JS engine (JIT-optimizable loop)
        // This is important on large datasets (thousands of locations) .
        const filteredLocs = []
        let totalQty = 0
        for (let i = 0; i < d.locations.length; i++) {
          const loc = d.locations[i]
          const qty = Number(loc.quantity ?? 0)
          if (qty > 0) {
            totalQty += qty
            filteredLocs.push(loc)
          }
        }
        matchingArticle.locations = filteredLocs
        matchingArticle.quantity = totalQty
      } else {
        pendingByPtProductId.set(Number(d.ptProductId), d)
      }
    }

    // if there arte new pt articles → fetch ptProduct details
    const newPtProductIds = Array.from(pendingByPtProductId.keys())
    if (newPtProductIds.length > 0) {
      // if this call fails, the whole function fetchInventoryStatus catch an error
      const newProductsData = await getPtProductsDetails(newPtProductIds)

      for (const p of newProductsData) {
        const articleDataEntry = pendingByPtProductId.get(Number(p.id))
        if (!articleDataEntry || articleDataEntry.locations.length === 0) {
          continue
        }

        const attributes: ArticleParams['attributes'] = buildAttributes(articleDataEntry, p)

        const card = cardDataMap.get(p.mkmProductId)
        if (!card) continue

        const invArticle = new InventoryArticle({
          attributes,
          card: card.toPlainObject(),
          locations: articleDataEntry.locations,
          ptProductId: p.id,
          ptArticleId: articleDataEntry.id,
          ptArticleReservedQuantity: articleDataEntry.reservedQuantity,
          marginTaxationQuantity: articleDataEntry.marginTaxationQuantity,
          standardTaxationQuantity: articleDataEntry.standardTaxationQuantity,
        })

        currentAll.set(invArticle.ptArticleId!, invArticle)
      }
    }

    // 2) remove article if qty is 0
    const updatedStock: InventoryArticle[] = []
    const updatedSealedStock: InventoryArticle[] = []

    for (const art of currentAll.values()) {
      if (art.quantity === 0) continue
      art.locations = art.locations?.filter((loc) => loc.quantity > 0) // filter all locations before pushing in updated stock
      if (isSingle(art.card)) updatedStock.push(art)
      else updatedSealedStock.push(art)
    }

    currentAll.clear()

    return { updatedStock, updatedSealedStock, maxProcessedAt }
  }

  const createMapFromBeResponse = (articleData: any[], locations: any[]) => {
    const ptArticles = new Map<number, any>()

    articleData.forEach((art) => {
      art.locations = []
      ptArticles.set(art.id, art)
    })

    locations.forEach((loc) => {
      const ptArticleId = Number(loc.ptArticleId)
      const article = ptArticles.get(ptArticleId)
      if (!loc.name.startsWith('Unknown-')) article.locations.push(loc)
    })

    return ptArticles
  }

  const fetchInventoryStatus = useCallback(
    async (idGame: number) => {
      if (isFetchingRef.current) return
      isFetchingRef.current = true

      try {
        const lastFetchedAt = fetchedStockRef.current.fetchedAt

        // fetchedAt is the processed-at cursor and can be months old on a rarely edited game:
        // staleness must be judged on when the stock was last loaded from the DB
        if (isInventoryStale(fetchedStockRef.current.lastFetchedFromDb)) {
          throw new Error('fetchedAtExpired')
        }

        let rawData
        if (lastFetchedAt !== '') rawData = await getInventoryStatusUpdate(idGame, lastFetchedAt)
        else rawData = await getInventoryStatusUpdate(idGame)

        const { articleData, locationData, isProcessing: processing, error } = rawData

        if (error) {
          if (error.toLowerCase().includes('invalid params')) {
            if (processing) {
              // 1: backend response "Invalid params" but there are some unprocessed pt transactions
              setStockLock(true)
              setIsProcessing(true)
              return true
            } else {
              // 2: Invalid params but no processing transactions
              throw new Error(error)
            }
          } else {
            // Others
            throw new Error(error)
          }
        }

        if (processing) {
          setStockLock(true)
          setIsProcessing(true)
          return true
        }

        // finished processing → update stock
        setIsProcessing(false)
        stopPolling()

        if (!articleData?.length) {
          setZustandStock({
            stock: fetchedStockRef.current.stock,
            sealedStock: fetchedStockRef.current.sealedStock,
            fetchedAt: fetchedStockRef.current.fetchedAt ?? new Date().toISOString(),
            lastFetchedFromDb: fetchedStockRef.current.lastFetchedFromDb,
          }) // I forcefully create a new reference so react updates
          setRefreshedStock(true)
          setStockLock(false)
          return false
        }

        const mapFromBeResponse = createMapFromBeResponse(articleData, locationData)

        const { updatedStock, updatedSealedStock, maxProcessedAt } = await mergeStock(
          fetchedStockRef.current,
          mapFromBeResponse
        )

        // avoids two Date allocations and maintains temporal consistency between the two stores.
        const now = new Date().toISOString()

        // synchronize the two setState in a single batch:
        unstable_batchedUpdates(() => {
          // diff fetches don't refresh lastFetchedFromDb (only full DB fetches do)
          setFetchedStock({
            stock: updatedStock,
            sealedStock: updatedSealedStock,
            fetchedAt: maxProcessedAt ?? now,
            lastFetchedFromDb: fetchedStockRef.current.lastFetchedFromDb,
          })
          setZustandStock({
            stock: updatedStock,
            sealedStock: updatedSealedStock,
            fetchedAt: maxProcessedAt ?? now,
            lastFetchedFromDb: fetchedStockRef.current.lastFetchedFromDb,
          })
          setRefreshedStock(true)
          setStockLock(false)
          mapFromBeResponse.clear()
        })

        return false
      } catch (error) {
        stopPolling()
        setIsProcessing(false)
        throw error
      } finally {
        isFetchingRef.current = false
        // return false
      }
    },
    [cardDataMap]
  )

  const startPolling = useCallback(
    (idGame: number) => {
      if (pollingRef.current) {
        console.warn('[Polling] already active, skipping start')
        return
      }

      pollingRef.current = setInterval(() => {
        fetchInventoryStatus(idGame).catch((err) => {
          onPollingError?.(err)
        })
      }, 1000)
    },
    [fetchInventoryStatus, onPollingError]
  )

  // cleanup when you leave the page
  useEffect(() => {
    return () => {
      stopPolling()
    }
  }, [stopPolling])

  return {
    fetchInventoryStatus,
    startPolling,
    stopPolling,
    isProcessing,
  }
}
